import { useState, useEffect } from 'react';
import {
  getSavedConsent,
  saveConsent,
  updateGtagConsent,
  type ConsentPreferences,
} from './GoogleAnalytics';

type Language = 'he' | 'en' | 'ru';

const translations: Record<Language, {
  message: string;
  acceptAll: string;
  rejectAll: string;
  privacyNote: string;
}> = {
  he: {
    message: 'אנו משתמשים בעוגיות כדי לשפר את חוויית הגלישה ולנתח את התנועה באתר.',
    acceptAll: 'אישור הכל',
    rejectAll: 'דחיית הכל',
    privacyNote: 'תוכלו לשנות את העדפותיכם בכל עת.',
  },
  en: {
    message: 'We use cookies to improve your browsing experience and analyze site traffic.',
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
    privacyNote: 'You can change your preferences at any time.',
  },
  ru: {
    message: 'Мы используем файлы cookie для улучшения работы сайта и анализа трафика.',
    acceptAll: 'Принять все',
    rejectAll: 'Отклонить все',
    privacyNote: 'Вы можете изменить свои настройки в любое время.',
  },
};

interface CookieConsentProps {
  language: Language;
}

export function CookieConsent({ language }: CookieConsentProps) {
  const [visible, setVisible] = useState(false);
  const t = translations[language];
  const isRTL = language === 'he';

  useEffect(() => {
    // Only show if user hasn't made a choice yet
    if (!getSavedConsent()) {
      setVisible(true);
    }
  }, []);

  const handleConsent = (granted: boolean) => {
    const state = granted ? 'granted' : 'denied';
    const prefs: ConsentPreferences = {
      analytics_storage: state,
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
    };
    saveConsent(prefs);
    updateGtagConsent(prefs);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(68, 68, 68, 0.97)',
        backdropFilter: 'blur(8px)',
        color: '#F4EED7',
        padding: '16px 20px',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.2)',
      }}
    >
      <div
        style={{
          maxWidth: '960px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '1.5' }}>
          {t.message}{' '}
          <span style={{ opacity: 0.7, fontSize: '12px' }}>{t.privacyNote}</span>
        </p>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            onClick={() => handleConsent(true)}
            style={{
              backgroundColor: '#F29B70',
              color: '#444444',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {t.acceptAll}
          </button>
          <button
            onClick={() => handleConsent(false)}
            style={{
              backgroundColor: 'transparent',
              color: '#F4EED7',
              border: '1px solid #F4EED7',
              borderRadius: '9999px',
              padding: '8px 24px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {t.rejectAll}
          </button>
        </div>
      </div>
    </div>
  );
}
